// @ts-nocheck
import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { flushSync } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { EditorContent, getMarkRange, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Link } from '@tiptap/extension-link';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { Close } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import {
  EMPTY_ARRAY,
  MENTIONED_ENTITIES,
  NOOP,
} from '../../../../const/CommonConst';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import useDocumentTemplate from '../../../../hooks/Configuration/useDocumentTemplate';
import CustomPopover from '../../CustomPopover';
import EditorToolBar from './EditorToolbar';
import HyperlinkModal from './HyperlinkModal';
import { MentionContext } from './MentionContext';
import {
  createUserMentionExtension,
  createModelMentionExtension,
  createArtifactMentionExtension,
  createDocumentMentionExtension,
  createAttributeMentionExtension,
} from './extensions/createMentionExtensions';
import { draftRawToTipTapHtml } from './utils/draftTipTapConverter';
import { normalizeHyperlink } from '../../../../utils/Utils';

/**
 * @function isDraftContent
 * @description Detects legacy DraftJS raw content in an object or serialized JSON string.
 * @param {Object|string} content - Incoming editor content.
 * @returns {boolean} Whether the content contains DraftJS blocks.
 */
function isDraftContent(content) {
  if (!content) return false;
  if (typeof content === 'object' && Array.isArray(content.blocks)) return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      return typeof parsed === 'object' && Array.isArray(parsed?.blocks);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * @function isTipTapContent
 * @description Detects a native TipTap document in an object or serialized JSON string.
 * @param {Object|string} content - Incoming editor content.
 * @returns {boolean} Whether the content is a TipTap document.
 */
function isTipTapContent(content) {
  if (!content) return false;
  if (typeof content === 'object' && content.type === 'doc') return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      return parsed?.type === 'doc';
    } catch {
      return false;
    }
  }
  return false;
}

/** Map MENTIONED_ENTITIES mentionType to TipTap node name */
const MENTION_TYPE_TO_NODE = {
  [MENTIONED_ENTITIES.user.mentionType]: 'userMention',
  [MENTIONED_ENTITIES.model.mentionType]: 'modelMention',
  [MENTIONED_ENTITIES.artifact.mentionType]: 'artifactMention',
  [MENTIONED_ENTITIES.document.mentionType]: 'documentMention',
  [MENTIONED_ENTITIES.attribute.mentionType]: 'attributeMention',
};

const EDITOR_CONTENT_UPDATE_DEBOUNCE_MS = 250;
const newTabIcon = new DOMParser()
  .parseFromString(renderToStaticMarkup(<OpenInNewIcon />), 'text/html')
  .querySelector('svg');
newTabIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
const NEW_TAB_ICON_URL = `url("data:image/svg+xml,${encodeURIComponent(
  new XMLSerializer().serializeToString(newTabIcon)
)}")`;

/**
 * @function stringifyEditorContent
 * @description Normalizes content to a string for synchronization checks, returning an empty string if serialization fails.
 * @returns {string} Serialized content or the original string.
 */
function stringifyEditorContent(content) {
  if (!content) return '';
  if (typeof content === 'string') return content;
  try {
    return JSON.stringify(content);
  } catch {
    return '';
  }
}

/**
 * @function hasTipTapText
 * @description Recursively checks for non-whitespace text to determine the editor's empty state.
 * @returns {boolean} Whether the document contains non-whitespace text.
 */
function hasTipTapText(node) {
  if (!node) return false;
  if (node.type === 'text') return Boolean(node.text?.trim());
  return (node.content || []).some(hasTipTapText);
}

/**
 * @component FilePreview
 * @description Displays an attached file name with a removal action.
 * @returns {JSX.Element} The attachment preview.
 */
const FilePreview = ({ file, handleRemoveFile }) => {
  const fileName = file?.name;
  const handleClick = useCallback(
    () => handleRemoveFile(file),
    [handleRemoveFile, file]
  );
  return (
    <Box
      sx={{
        display: 'flex',
        border: (theme) => `1px solid ${theme.palette.other.attachmentBorder}`,
        width: '200px',
        borderRadius: '4px',
        position: 'relative',
      }}
    >
      <Box display="flex" alignItems="center" height={60} width={60}>
        <InsertDriveFileIcon
          sx={{ height: '100%', width: '100%' }}
          color="primary"
        />
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Typography
          variant="body1"
          component="div"
          overflow="hidden"
          textOverflow="ellipsis"
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'nowrap',
            maxWidth: '140px',
          }}
          color="text.primary"
          title={fileName}
        >
          {fileName}
        </Typography>
      </Box>
      <Box sx={{ position: 'absolute', top: -18, right: -23 }}>
        <IconButton
          color="primary"
          aria-label="remove document"
          onClick={handleClick}
        >
          <CancelIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

FilePreview.propTypes = {
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
};

/**
 * @component TextEditor
 * @description Edits or displays rich content with mentions, attachments, and hyperlinks, including legacy DraftJS content conversion.
 * @param {Object} props - Editor content, callbacks, and feature configuration.
 * @param {boolean} [props.enableHyperlinks=true] - Enables hyperlink insertion and automatic linking; stored links remain visible when disabled.
 * @returns {JSX.Element} The rich-text editor and its applicable controls.
 * @example <TextEditor content={description} onChange={setDescription} enableHyperlinks />
 * @example <TextEditor content={description} readOnly />
 */
const TextEditor = ({
  content = '',
  onChange = NOOP,
  userList = EMPTY_ARRAY,
  threadForm = null,
  transitionStates = EMPTY_ARRAY,
  readOnly = false,
  disableTagAndTitleFields = false,
  entityType,
  entityId,
  isEditorEmpty,
  handleSubmit,
  mode = 'default',
  cancelButton = null,
  submitButton = null,
  isUserAuthorizedForAddNote,
  onClose = null,
  compact = false,
  files: parentFiles,
  onAttachFile,
  onRemoveFile,
  showAttachment = false,
  placeholder = 'Write your message here *',
  selectedEntityToInsert = null,
  enableHyperlinks = false,
}) => {
  const [linkDialog, setLinkDialog] = useState(null);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const [internalFiles, setInternalFiles] = useState([]);
  const files = parentFiles || internalFiles;

  const editorScrollRef = useRef(null);
  // Tracks the last DraftJS JSON string emitted via onChange to prevent sync loops
  const lastEmittedRef = useRef(null);
  const latestContentRef = useRef(stringifyEditorContent(content));
  const lastInsertedEntityRef = useRef(null);
  const hasPendingContentChangeRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const handleSubmitRef = useRef(handleSubmit);
  onChangeRef.current = onChange;
  handleSubmitRef.current = handleSubmit;
  const [localIsEditorEmpty, setLocalIsEditorEmpty] = useState(isEditorEmpty);

  const { searchMentionEntities, fetchDocumentDetails, getAttributeDetails } =
    useModelInventory();
  const { fetchSharableLink } = useDocumentTemplate();

  const mentionContextValue = useMemo(
    () => ({ fetchDocumentDetails, fetchSharableLink, getAttributeDetails }),
    [fetchDocumentDetails, fetchSharableLink, getAttributeDetails]
  );

  // Determine initial content for TipTap (only computed on mount).
  // TipTap JSON is passed as-is; legacy DraftJS is converted to HTML; plain HTML stays.
  const initialContent = useMemo(() => {
    if (!content) return '';
    if (isTipTapContent(content)) {
      return typeof content === 'string' ? JSON.parse(content) : content;
    }
    if (isDraftContent(content)) return draftRawToTipTapHtml(content);
    return content;
  }, []); // intentionally only on mount

  const extensions = useMemo(
    () => [
      StarterKit,
      Link.configure({
        openOnClick: false,
        defaultProtocol: 'https',
        autolink: enableHyperlinks,
        linkOnPaste: enableHyperlinks,
        isAllowedUri: (url) => Boolean(normalizeHyperlink(url)),
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'text-editor-hyperlink',
        },
      }),
      Underline,
      Image.configure({ allowBase64: true }),
      Placeholder.configure({ placeholder }),
      createUserMentionExtension({ userList }),
      createModelMentionExtension({
        searchMentionEntities,
        entityType,
        entityId,
      }),
      createArtifactMentionExtension({
        searchMentionEntities,
        entityType,
        entityId,
      }),
      createDocumentMentionExtension({
        searchMentionEntities,
        entityType,
        entityId,
      }),
      createAttributeMentionExtension({
        searchMentionEntities,
        entityType,
        entityId,
      }),
    ],
    // Extensions recreated only when entity context changes
    [entityType, entityId, enableHyperlinks]
  );

  /**
   * @function emitContentChange
   * @description Notifies the parent of serialized content and optionally flushes React updates before a dependent action.
   */
  const emitContentChange = useCallback(
    (nextContent, { sync = false } = {}) => {
      lastEmittedRef.current = nextContent;
      hasPendingContentChangeRef.current = false;
      const notifyParent = () => onChangeRef.current(nextContent);
      if (sync) {
        flushSync(notifyParent);
        return;
      }
      notifyParent();
    },
    []
  );

  const debouncedEmitContentChange = useMemo(
    () =>
      debounce((nextContent) => {
        emitContentChange(nextContent);
      }, EDITOR_CONTENT_UPDATE_DEBOUNCE_MS),
    [emitContentChange]
  );

  /**
   * @function serializeEditorContent
   * @description Serializes the active TipTap document and updates the latest-content cache and local empty state.
   * @returns {string} Serialized editor content.
   */
  const serializeEditorContent = useCallback((editorInstance) => {
    if (!editorInstance) return latestContentRef.current || '';
    const nextJson = editorInstance.getJSON();
    const nextContent = JSON.stringify(nextJson);
    latestContentRef.current = nextContent;
    setLocalIsEditorEmpty((prev) => {
      const nextIsEmpty = !hasTipTapText(nextJson);
      return prev === nextIsEmpty ? prev : nextIsEmpty;
    });
    return nextContent;
  }, []);

  /**
   * @function flushPendingContentChange
   * @description Cancels the debounce and emits pending content so blur and submit actions use the latest document.
   * @returns {string} The latest serialized content.
   */
  const flushPendingContentChange = useCallback(
    (editorInstance, { sync = false } = {}) => {
      if (!hasPendingContentChangeRef.current) {
        debouncedEmitContentChange.cancel();
        return latestContentRef.current || '';
      }
      const nextContent =
        latestContentRef.current || serializeEditorContent(editorInstance);
      debouncedEmitContentChange.cancel();
      emitContentChange(nextContent, { sync });
      return nextContent;
    },
    [debouncedEmitContentChange, emitContentChange, serializeEditorContent]
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor: e }) => {
      const json = serializeEditorContent(e);
      lastEmittedRef.current = json;
      hasPendingContentChangeRef.current = true;
      debouncedEmitContentChange(json);
    },
    onBlur: ({ editor: e }) => {
      flushPendingContentChange(e, { sync: true });
    },
    editorProps: {
      handleDOMEvents: {
        click: (_view, event) => {
          const anchor = event.target?.closest?.('a.text-editor-hyperlink');
          if (!anchor) {
            return false;
          }
          event.preventDefault();
          if (readOnlyRef.current || event.ctrlKey || event.metaKey) {
            event.stopPropagation();
            const href = normalizeHyperlink(anchor.getAttribute('href'));
            if (href) {
              window.open(href, '_blank', 'noopener,noreferrer');
            }
          }
          return true;
        },
      },
      handleKeyDown: (_view, event) => {
        const anchor = event.target?.closest?.('a.text-editor-hyperlink');
        if (readOnlyRef.current && anchor && event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          const href = normalizeHyperlink(anchor.getAttribute('href'));
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
          }
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        // eslint-disable-next-line no-shadow
        const files = Array.from(event.clipboardData?.files || []);
        if (!files.length || !files[0].type.startsWith('image/')) return false;
        const file = files[0];
        const reader = new FileReader();
        reader.onload = () => {
          const img = new window.Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxSize = 1200;
            let { width, height } = img;
            if (width > height && width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            } else if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            const resizedBase64 = canvas.toDataURL('image/jpeg', 0.9);
            editor?.chain().focus().setImage({ src: resizedBase64 }).run();
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
        return true;
      },
    },
  });

  useEffect(
    () => () => {
      debouncedEmitContentChange.cancel();
    },
    [debouncedEmitContentChange]
  );

  useEffect(() => {
    setLocalIsEditorEmpty(isEditorEmpty);
  }, [isEditorEmpty]);

  // Keep the existing TipTap instance in sync when Chat Forum toggles read/edit.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
    if (!readOnly) {
      setTimeout(() => editor.commands.focus(), 0);
    }
  }, [editor, readOnly]);

  // Auto-scroll to bottom on content change
  useEffect(() => {
    if (editorScrollRef?.current) {
      editorScrollRef.current.scrollTop = editorScrollRef.current.scrollHeight;
    }
  }, [content]);

  // Sync external content changes (e.g. parent resets editor after submit)
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    // Skip content we just emitted — avoids the onChange→setContent loop
    if (content === lastEmittedRef.current) return;
    debouncedEmitContentChange.cancel();
    hasPendingContentChangeRef.current = false;

    let incoming;
    if (isTipTapContent(content)) {
      incoming = typeof content === 'string' ? JSON.parse(content) : content;
    } else if (isDraftContent(content)) {
      incoming = draftRawToTipTapHtml(content);
    } else {
      incoming = content || '';
    }

    const currentJson = JSON.stringify(editor.getJSON());
    const incomingStr =
      typeof incoming === 'object' ? JSON.stringify(incoming) : incoming;
    latestContentRef.current = stringifyEditorContent(incoming);

    if (currentJson !== incomingStr) {
      editor.commands.setContent(incoming, false);
    }
  }, [content, debouncedEmitContentChange, editor]);

  // Programmatic mention insertion (for AddNoteModal / pre-filled mentions)
  useEffect(() => {
    if (!selectedEntityToInsert) {
      lastInsertedEntityRef.current = null;
      return;
    }
    if (!editor) return;
    const insertionKey = stringifyEditorContent(selectedEntityToInsert);
    if (lastInsertedEntityRef.current === insertionKey) return;
    const nodeType = MENTION_TYPE_TO_NODE[selectedEntityToInsert.mentionType];
    if (!nodeType) return;

    let attrs = {};
    if (selectedEntityToInsert.user) {
      attrs = {
        id: selectedEntityToInsert.id || selectedEntityToInsert.user,
        label: selectedEntityToInsert.name || selectedEntityToInsert.user,
        name: selectedEntityToInsert.name || selectedEntityToInsert.user,
        designation: selectedEntityToInsert.designation || '',
        role: selectedEntityToInsert.role || '',
        user: selectedEntityToInsert.user || '',
        user_email: selectedEntityToInsert.user_email || '',
      };
    } else {
      attrs = {
        id: selectedEntityToInsert.id,
        label:
          `${selectedEntityToInsert.uniqueId || ''} ${selectedEntityToInsert.name || ''}`.trim(),
        uniqueId: selectedEntityToInsert.uniqueId,
        name: selectedEntityToInsert.name,
        entityType: selectedEntityToInsert.entityType,
        parentEntityId: selectedEntityToInsert.entityId,
        entityId: selectedEntityToInsert.id,
        requestType: selectedEntityToInsert.requestType || '',
      };
    }

    editor
      .chain()
      .focus()
      .insertContent({ type: nodeType, attrs })
      .insertContent(' ')
      .run();
    lastInsertedEntityRef.current = insertionKey;
  }, [editor, selectedEntityToInsert]);

  const handleAttachFile = useCallback(
    (file) => {
      if (onAttachFile) {
        onAttachFile(file);
      } else {
        setInternalFiles((prev) => [...prev, ...file]);
      }
    },
    [onAttachFile]
  );

  const handleRemoveFile = useCallback(
    (file) => {
      if (onRemoveFile) {
        onRemoveFile(file);
      } else {
        setInternalFiles((prev) => prev?.filter((f) => f?.name !== file?.name));
      }
    },
    [onRemoveFile]
  );

  /**
   * @function handleToolbarSubmit
   * @description Flushes pending content synchronously before invoking the parent's submit callback.
   */
  const handleToolbarSubmit = useCallback(
    (event) => {
      flushPendingContentChange(editor, { sync: true });
      handleSubmitRef.current?.(event);
    },
    [editor, flushPendingContentChange]
  );

  /**
   * @function handleSubmitButtonMouseDownCapture
   * @description Flushes pending content before a custom submit button's click handler runs.
   */
  const handleSubmitButtonMouseDownCapture = useCallback(() => {
    flushPendingContentChange(editor, { sync: true });
  }, [editor, flushPendingContentChange]);

  const submitButtonWithFlush = useMemo(() => {
    if (!submitButton) return submitButton;
    return (
      <Box
        component="span"
        onMouseDownCapture={handleSubmitButtonMouseDownCapture}
      >
        {submitButton}
      </Box>
    );
  }, [handleSubmitButtonMouseDownCapture, submitButton]);

  const handleGetOptionLabel = useCallback((option) => option, []);
  const handleOptionEqualToValue = useCallback(
    (option, value) => option === value,
    []
  );

  const handleChangeTag = useCallback(
    (_event, newValue) => {
      threadForm.setFieldValue('tag', newValue);
    },
    [threadForm]
  );

  const handleOnBlurTag = useCallback(
    (e) => {
      threadForm.setFieldValue('tag', e.target.value);
    },
    [threadForm]
  );

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        label="Workflow state or custom tag"
        placeholder="Select state or add custom tag."
        error={Boolean(threadForm?.errors?.tag)}
        helperText={threadForm?.errors?.tag}
        sx={{ wordBreak: 'break-word', whiteSpace: 'wrap' }}
        spellCheck={true}
      />
    ),
    [threadForm]
  );

  const renderTitleAndTags = () => {
    const shouldRender = !disableTagAndTitleFields && threadForm && !readOnly;
    if (!shouldRender) return null;
    return (
      <Box display="flex" width="100%" alignItems="center">
        <Grid container spacing="8px" margin="8px">
          <Grid item xs={8}>
            <TextField
              required
              name="title"
              value={threadForm?.values?.title}
              {...threadForm.getFieldProps('title')}
              placeholder="Enter thread title"
              label="Thread title"
              helperText={
                threadForm?.errors?.title && threadForm?.touched?.title
                  ? threadForm.errors.title
                  : null
              }
              error={
                Boolean(threadForm?.errors?.title) && threadForm?.touched?.title
              }
              spellCheck={true}
            />
          </Grid>
          <Grid item xs={4}>
            <Autocomplete
              id="select_tag"
              options={transitionStates}
              disableClearable={['', undefined].includes(
                threadForm?.values?.tag
              )}
              value={threadForm?.values?.tag}
              freeSolo
              getOptionLabel={handleGetOptionLabel}
              isOptionEqualToValue={handleOptionEqualToValue}
              onChange={handleChangeTag}
              onBlur={handleOnBlurTag}
              popupIcon={<DropdownArrow />}
              renderInput={renderInput}
              PopperComponent={CustomPopover}
            />
          </Grid>
        </Grid>
        {onClose && (
          <IconButton
            onClick={onClose}
            title="Close"
            variant="outlined"
            aria-label="Close editor"
            sx={{ position: 'absolute', right: '16px', top: '14px' }}
          >
            <Close />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderFilesView = () =>
    files?.length > 0 ? (
      <Stack direction="row" gap={2} p={2}>
        {files.map((file) => (
          <FilePreview
            key={file?.name}
            file={file}
            handleRemoveFile={handleRemoveFile}
          />
        ))}
      </Stack>
    ) : null;

  const handleEditorRootClick = useCallback(() => {
    if (!readOnly) {
      editor?.commands.focus();
    }
  }, [editor, readOnly]);

  /**
   * @function handleAddLink
   * @description Captures a supported selection and opens the hyperlink dialog only when link editing is enabled.
   */
  const handleAddLink = useCallback(() => {
    if (!editor || editor.isDestroyed || readOnly || !enableHyperlinks) {
      return;
    }
    const { from, to, $from, $to } = editor.state.selection;
    if (!$from.sameParent($to) || !$from.parent.isTextblock) {
      return;
    }
    const linkType = editor.schema.marks.link;
    const range = getMarkRange($from, linkType);
    const existing = range && to <= range.to ? range : null;
    const target = existing || { from, to };
    let containsAtom = false;
    editor.state.doc.nodesBetween(target.from, target.to, (node) => {
      if (node.isInline && !node.isText) {
        containsAtom = true;
      }
    });
    if (containsAtom) {
      return;
    }
    const mark = existing
      ? editor.state.doc
          .resolve(existing.from + 1)
          .marks()
          .find((item) => item.type === linkType)
      : null;
    setLinkDialog({
      ...target,
      linkText: editor.state.doc.textBetween(target.from, target.to, ''),
      linkUrl: mark?.attrs?.href || '',
      editing: Boolean(existing),
    });
  }, [editor, readOnly, enableHyperlinks]);

  /**
   * @function handleCloseLinkDialog
   * @description Closes the dialog without modifying content and restores the saved editor selection and focus.
   */
  const handleCloseLinkDialog = useCallback(() => {
    setLinkDialog(null);
    if (editor && !editor.isDestroyed && !readOnly) {
      if (linkDialog) {
        editor.commands.setTextSelection({
          from: linkDialog.from,
          to: linkDialog.to,
        });
      }
      editor.commands.focus();
    }
  }, [editor, readOnly, linkDialog]);

  /**
   * @function handleSubmitLink
   * @description Applies submitted values to the saved link range and flushes content changes before closing the dialog.
   */
  const handleSubmitLink = useCallback(
    ({ linkText, linkUrl }) => {
      if (
        !editor ||
        editor.isDestroyed ||
        !linkDialog ||
        readOnly ||
        !enableHyperlinks
      ) {
        return;
      }
      const href = normalizeHyperlink(linkUrl);
      const { from, to, editing } = linkDialog;
      if (href === null || from < 0 || to > editor.state.doc.content.size) {
        return;
      }
      const currentText = editor.state.doc.textBetween(from, to, '');
      const chain = editor.chain().focus().setTextSelection({ from, to });
      const preserveText =
        linkText === currentText || (editing && !href && !linkText.trim());
      if (preserveText) {
        if (href) {
          chain.setLink({ href });
        } else {
          chain.unsetLink();
        }
      } else if (linkText) {
        const marks = editor.state.doc
          .resolve(from)
          .marks()
          .filter((mark) => mark.type.name !== 'link')
          .map((mark) => mark.toJSON());
        if (href) {
          marks.push({ type: 'link', attrs: { href } });
        }
        chain.insertContentAt(
          { from, to },
          { type: 'text', text: linkText, marks }
        );
      } else {
        return;
      }
      const end = preserveText ? to : from + linkText.length;
      chain
        .setTextSelection(end)
        .command(({ tr }) => {
          tr.removeStoredMark(editor.schema.marks.link);
          return true;
        })
        .run();
      flushPendingContentChange(editor, { sync: true });
      setLinkDialog(null);
    },
    [editor, linkDialog, readOnly, enableHyperlinks, flushPendingContentChange]
  );

  useEffect(() => {
    if (readOnly || !enableHyperlinks) {
      setLinkDialog(null);
    }
  }, [readOnly, enableHyperlinks]);

  const editableMinHeight = compact ? '60px' : '20vh';
  const editablePadding = compact ? '12px 16px' : '25px';
  const editorPadding = readOnly ? 0 : editablePadding;
  const editorMinHeight = readOnly ? 0 : editableMinHeight;
  const editorRootMinHeight = readOnly ? '0vh' : editableMinHeight;

  return (
    <MentionContext.Provider value={mentionContextValue}>
      <Box
        className={`RichEditorOuterBox ${readOnly ? 'read-only' : 'edit-only'}`}
        sx={{
          '&&.read-only .text-editor-hyperlink::after': {
            content: '""',
            display: 'inline-block',
            width: '12px',
            height: '12px',
            verticalAlign: 'text-bottom',
            backgroundColor: 'currentColor',
            mask: `${NEW_TAB_ICON_URL} center / contain no-repeat`,
            WebkitMask: `${NEW_TAB_ICON_URL} center / contain no-repeat`,
          },
        }}
      >
        {renderTitleAndTags()}
        <Box
          onClick={handleEditorRootClick}
          ref={editorScrollRef}
          sx={{
            '& .ProseMirror': {
              padding: editorPadding,
              outline: 'none',
              minHeight: editorMinHeight,
            },
            '& .ProseMirror ol': {
              listStyleType: 'decimal',
              paddingLeft: '1.5em',
            },
            '& .ProseMirror ul': { paddingLeft: '1.5em' },
            '& .ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              color: (theme) => theme.palette.text.disabled,
              pointerEvents: 'none',
              float: 'left',
              height: 0,
            },
            minHeight: editorRootMinHeight,
            maxHeight: compact ? '20vh' : '40vh',
            overflow: 'auto',
          }}
          className="RichEditor-root"
        >
          <EditorContent
            editor={editor}
            aria-label="Message editor"
            spellCheck={true}
          />
        </Box>
        {!readOnly && (
          <EditorToolBar
            editor={editor}
            handleAttachFile={handleAttachFile}
            isEditorEmpty={localIsEditorEmpty}
            handleSubmit={handleSubmit ? handleToolbarSubmit : handleSubmit}
            mode={mode}
            cancelButton={cancelButton}
            submitButton={submitButtonWithFlush}
            isUserAuthorizedForAddNote={isUserAuthorizedForAddNote}
            showAttachment={showAttachment}
            enableHyperlinks={enableHyperlinks}
            handleAddLink={handleAddLink}
          />
        )}
        {renderFilesView()}
        {linkDialog && !readOnly && enableHyperlinks && (
          <HyperlinkModal
            {...linkDialog}
            onClose={handleCloseLinkDialog}
            onSubmit={handleSubmitLink}
          />
        )}
      </Box>
    </MentionContext.Provider>
  );
};

TextEditor.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  userList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  transitionStates: PropTypes.arrayOf(PropTypes.string),
  threadForm: PropTypes.oneOfType([PropTypes.object]),
  entityType: PropTypes.string,
  entityId: PropTypes.number,
  readOnly: PropTypes.bool,
  disableTagAndTitleFields: PropTypes.bool,
  isEditorEmpty: PropTypes.bool,
  handleSubmit: PropTypes.func,
  mode: PropTypes.string,
  cancelButton: PropTypes.node,
  submitButton: PropTypes.node,
  isUserAuthorizedForAddNote: PropTypes.bool,
  onClose: PropTypes.func,
  compact: PropTypes.bool,
  showAttachment: PropTypes.bool,
  files: PropTypes.oneOfType([PropTypes.object]),
  onAttachFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
  placeholder: PropTypes.string,
  selectedEntityToInsert: PropTypes.oneOfType([PropTypes.object]),
  enableHyperlinks: PropTypes.bool,
};

export default TextEditor;
